import { Alert } from "@mui/lab";
import { AppBar, Card, CardContent, Grid, Toolbar } from "@mui/material";
import { withStyles, WithStyles } from "@mui/styles";
import * as api from "api/Core";
import { FormStyleClasses, GlobalClassKeys } from "assets/jss/common-styles";
import ButtonCancel from "components/Buttons/ButtonCancel";
import ButtonSave from "components/Buttons/ButtonSave";
import { RequestIndicator } from "components/CallServer";
import RequestErrorDialog from "components/CallServer/RequestErrorDialog";
import { RequestErrorReport } from "components/CallServer/RequestErrorReport";
import AttachSelection, { ConvertUploadToFileThumbnaildGetDto, UploadingStatus } from "components/FileUpload/AttachSelection";
import CardHeaderColor from "components/MuiExtras/Card/CardHeaderColor";
import MuiContainer from "components/MuiExtras/MuiContainer";
import QuillTextBox from "components/Quill/QuillTextBox";
import { RecordAbout } from "components/RecordAbout";
import { WithSnackbar, withSnackbar } from 'components/SnackbarProvider/Snackbar';
import { Formaters } from "lib/Formarters";
import LibUtils from "lib/LibUtils";
import { AspectRatio } from "models/ApiEnums";
import { AsyncUploadModel, ContentMetadata, FileThumbnaildGetDto, GetSetupAgreementDto, SaveSetupAgreementDto } from "models/ApiModels";
import { AttachType, RequestLifeCycle } from "models/AppModels";
import * as React from "react";
import { Trans, WithTranslation, withTranslation } from "react-i18next";
import { Prompt } from "react-router";
import { ISetupProps } from "views/System/Components/SetupAdminEdit";
import SetupAgreementLogDialog from "views/System/Components/SetupAgreementLogDialog";

/////////////////////////////////////////////////////////////////////////////////////

interface IProps {
}

interface IState {
    dialog: any,
    formCycle: RequestLifeCycle,
    lastError: any,
    record: GetSetupAgreementDto,
    uploadingStatus: UploadingStatus;
}

type Props = ISetupProps & IProps & WithStyles<typeof FormStyleClasses> & WithSnackbar & WithTranslation;

/////////////////////////////////////////////////////////////////////////////////////

class SetupAgreementEdit extends React.Component<Props, IState> {

    private compareDto: any = null;
    private loading: boolean;

    ///
    constructor(props: Props) {
        super(props);

        this.compareDto = null;
        this.loading = false;

        this.state = {
            formCycle: RequestLifeCycle.Init,
            lastError: null,
            dialog: null,
            record: {
                file: null,
                text: null,
                recordInfo: null
            },
            uploadingStatus: "none",
        };
    }


    ///
    private res(r: string, f: any = null) {
        return this.props.t("Views.System.Components.SetupAgreementEdit." + r, f);
    }

    ///
    private load() {
        this.loading = true;

        api.getSetupAgreement()
            .then((dto) => {

                if (dto == null) dto = this.state.record;

                this.compareDto = LibUtils.deepCopy(dto);

                this.setState({
                    formCycle: RequestLifeCycle.InitSuccess,
                    record: dto
                }, () => { this.props.onEditing(false); this.loading = false; });
            })
            .catch((err: any) => {
                this.setState({
                    formCycle: RequestLifeCycle.InitFailed
                });
            });
    }

    ///
    private getPostDto(): SaveSetupAgreementDto {
        const { record } = this.state;

        return {
            text: record.text,
            file: record.file == null ? null : record.file.mainFile,
        };
    }

    ///
    private updateRecord(name: string, v: any) {
        if (this.loading) return;

        this.setState(state => {
            switch (name) {
                case "text":
                    state.record.text = v;
                    break;
                case "file":
                    state.record.file = v;
                    break;
            }
            return state;
        }, () => this.props.onEditing(this.isRecordChanged()));
    }

    ///
    private formIsValid() {
        return true;
    }

    ///
    private handleSubmit(event: React.MouseEvent<HTMLButtonElement>) {
        event.preventDefault();

        if (!this.formIsValid())
            return;

        const dto = this.getPostDto();

        this.setState({
            formCycle: RequestLifeCycle.Posting,
        });

        api.saveSetupAgreement(dto)
            .then((response) => {
                this.compareDto = LibUtils.deepCopy(this.state.record);
                this.props.snackbar.showMessage(this.props.t("Common.Literals.UpdateSuccess"), "success");
                this.setState({ formCycle: RequestLifeCycle.PostSuccess }, () => this.props.onEditing(false));
            })
            .catch((err: any) => {
                this.setState({ lastError: err, formCycle: RequestLifeCycle.PostFailed });
            });
    }

    ///
    private isRecordChanged() {
        return this.state.lastError == null && (this.state.record.file != this.compareDto?.file || this.state.record.text != this.compareDto?.text);
    }

    ///
    private handleFileUploaded(e: AsyncUploadModel) {
        this.updateRecord("file", ConvertUploadToFileThumbnaildGetDto(e));
    }

    ///
    private handleUploadRemove(e: FileThumbnaildGetDto | ContentMetadata) {
        if ((e as FileThumbnaildGetDto).mainFile) {
            this.updateRecord("file", null);
        }
    }

    ///
    private handleUploadingStatus = (status: UploadingStatus) => {
        this.setState({ uploadingStatus: status });
    };

    ///
    private renderToolbar() {
        return (
            <AppBar position="sticky" color="default" className={GlobalClassKeys.stickyToolbar}  >
                <Toolbar>
                    <div className={GlobalClassKeys.pushRight}>
                        {
                            this.isRecordChanged() &&
                            <ButtonCancel
                                onClick={(e: React.MouseEvent<HTMLButtonElement>) => { e.preventDefault(); this.load(); }}
                            />
                        }
                        <ButtonSave
                            disabled={!this.formIsValid()}
                            onClick={(e: React.MouseEvent<HTMLButtonElement>) => this.handleSubmit(e)}
                        />

                    </div>
                </Toolbar>
            </AppBar>
        );
    }

    ///
    private renderRecordInfo() {
        return this.state.record.recordInfo
            ? (
                <MuiContainer margin="normal">
                    <RecordAbout
                        record={this.state.record.recordInfo}
                        hideCreator={true}
                        onShowHistory={
                            () => {
                                this.setState({
                                    dialog:
                                        <SetupAgreementLogDialog
                                            onClose={() => this.setState({ dialog: null })}
                                        />
                                });
                            }
                        }
                    />
                </MuiContainer>
            )
            : null;
    }

    ///
    private renderFormMain() {
        const { record } = this.state;

        return (
            <MuiContainer margin="normal">

                <MuiContainer margin="normal">
                    <Card>
                        <CardHeaderColor color="primary" title={this.res("LicenceTitle")} />
                        <CardContent>

                            <MuiContainer>
                                <QuillTextBox
                                    toolbar={true}
                                    height={"20em"}
                                    content={record.text}
                                    onChange={(content) => this.updateRecord("text", content)}
                                    placeholder={this.res("LicencePlaceholder")}
                                />
                            </MuiContainer>

                            <Alert severity="warning" >
                                <div style={{ display: "inline" }}>
                                    <Trans
                                        i18nKey="Views.System.Components.SetupAgreementEdit.LicenceMessage"
                                        components={[<b></b>, <span></span>]}
                                    />
                                </div>
                            </Alert>

                        </CardContent>
                    </Card>
                </MuiContainer>

                <Card>
                    <CardHeaderColor color="primary" title={this.res("FileLicenceTitle")} />
                    <CardContent>
                        <Grid container justifyContent="center" spacing={2}>
                            <Grid item>
                                <AttachSelection
                                    aspectRatio={AspectRatio.Square}
                                    acceptedTypes={[AttachType.File]}
                                    attach={record.file == null ? [] : [record.file]}
                                    dropZoneText={this.res("FileLicenceDropzoneText")}
                                    onUploadComplete={(e) => this.handleFileUploaded(e)}
                                    onUploadRemove={(e) => this.handleUploadRemove(e)}
                                    onUploadStatusChange={this.handleUploadingStatus}
                                />
                            </Grid>
                            <Grid item xs>

                                {
                                    record.file &&
                                    <MuiContainer>
                                        <ul>
                                            <li>{record.file.mainFile.metadata.filename}</li>
                                            <li>{Formaters.fileSize(record.file.mainFile.metadata.fileSize)}</li>
                                        </ul>
                                    </MuiContainer>
                                }

                                <Alert severity="warning">
                                    <div style={{ display: "inline" }}>
                                        <Trans
                                            i18nKey="Views.System.Components.SetupAgreementEdit.FileLicenceMessage"
                                            components={[<b></b>, <span></span>]}
                                        />
                                    </div>
                                </Alert>

                            </Grid>
                        </Grid>
                    </CardContent>
                </Card>

            </MuiContainer>
        );
    }

    ///
    public componentDidMount() {
        this.load();
    }

    ///
    public componentWillUnmount() {
        window.onbeforeunload = undefined;
    }

    ///
    public componentDidUpdate() {
        if (this.isRecordChanged()) {
            window.onbeforeunload = () => true;
        } else {
            window.onbeforeunload = undefined;
        }
    }

    ///
    render() {

        if (this.state.formCycle == RequestLifeCycle.Init) {
            return <RequestIndicator type="waiting" />;
        }

        if (this.state.formCycle == RequestLifeCycle.InitFailed) {
            return <RequestErrorReport error={this.state.lastError} />;
        }

        return (
            <Grid container justifyContent="space-around">
                <Grid item xs={12} md={10} lg={8}>

                    {this.state.formCycle == RequestLifeCycle.Posting && <RequestIndicator type="post" />}

                    <RequestErrorDialog
                        open={this.state.formCycle == RequestLifeCycle.PostFailed}
                        error={this.state.lastError}
                        onClose={() => this.setState({ lastError: null, formCycle: RequestLifeCycle.None })}
                    />


                    {this.state.dialog}

                    {this.renderToolbar()}

                    <form noValidate autoComplete="nope" >
                        {this.renderFormMain()}
                    </form>

                    {this.renderRecordInfo()}

                    <Prompt
                        when={this.isRecordChanged()}
                        message={this.props.t("Common.Question.Unsave")}
                    />
                </Grid>
            </Grid>
        );
    }
}

export default withSnackbar(withTranslation()(withStyles(FormStyleClasses)(SetupAgreementEdit)));